import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLoading } from "../contexts/LoadingContext";
import { useRenderGuard } from "../hooks/useRenderGuard";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { HourglassSpinnerWrapper } from "./HourglassSpinnerWrapper";
// import { CompactExtensionPrompt } from './CompactExtensionPrompt'; // disabled for now
import { HelpResourcesMenu } from "./HelpResourcesMenu";
import { TourToast } from "./TourToast";
import { ExtensionBanner } from "./ExtensionBanner";
import { ExtensionComingSoonBanner } from "./ExtensionComingSoonBanner";
import { ProfileCompletionGuide } from "./ProfileCompletionGuide";
import { ResumeRequirementModal } from "./ResumeRequirementModal";
import { useResumeRequirement } from "../hooks/useResumeRequirement";
import "./MainContent.css";

interface MainContentProps {
  children: React.ReactNode;
  activeSection?: string;
  sidebarOverrides?: {
    loadingStates?: Record<string, boolean>;
  };
}

export const MainContent: React.FC<MainContentProps> = ({
  children,
  activeSection,
  sidebarOverrides,
}) => {
  useRenderGuard("MainContent");
  const { profile, loading, isAuthenticated } = useAuth();
  const { loadingStates } = useLoading();
  const { subscription, isLoadingSubscription, isLoadingPlans } =
    useSubscriptionContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasResume, isLoading: isLoadingResumes } = useResumeRequirement();
  const [resumeModalDismissed, setResumeModalDismissed] = useState(false);

  // Refs to track latest values for setTimeout callbacks (prevents stale closures)
  const subscriptionRef = useRef(subscription);
  const locationRef = useRef(location.pathname);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isLoadingSubscriptionRef = useRef(isLoadingSubscription);
  const loadingRef = useRef(loading);

  // Update refs whenever values change
  useEffect(() => {
    subscriptionRef.current = subscription;
    locationRef.current = location.pathname;
    isAuthenticatedRef.current = isAuthenticated;
    isLoadingSubscriptionRef.current = isLoadingSubscription;
    loadingRef.current = loading;
  }, [
    subscription,
    location.pathname,
    isAuthenticated,
    isLoadingSubscription,
    loading,
  ]);

  // Mobile menu state (only affects mobile behavior, desktop sidebar remains always visible)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add body class when MainContent (with sidebar) is present for reliable CSS targeting
  useEffect(() => {
    document.body.classList.add("has-sidebar");
    return () => {
      document.body.classList.remove("has-sidebar");
    };
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Helper function to check if route is public (handles trailing slashes and subpaths)
  const isPublicRoute = (pathname: string): boolean => {
    const publicRoutePrefixes = [
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/developer/api-integration-check",
    ];
    return publicRoutePrefixes.some((route) => pathname.startsWith(route));
  };

  // Check if current route is public (no subscription loading needed)
  const isPublic = isPublicRoute(location.pathname);

  // Redirect to login if not authenticated (but not if already on login page or other public routes)
  useEffect(() => {
    if (!loading && (!profile || !isAuthenticated) && !isPublic) {
      navigate("/login");
    }
  }, [loading, profile, isAuthenticated, navigate, isPublic]);

  // No forced redirect to purchase page: token balance gates features via API + toasts.

  // Show loading if:
  // - Auth is still loading, OR
  // - User is authenticated and subscription row is still loading (not on token purchase page)
  const isServicePlansPage = location.pathname === "/service-plans";
  const shouldShowLoading =
    loading ||
    (isAuthenticated &&
      !isPublic &&
      !isServicePlansPage &&
      isLoadingSubscription);

  // Removed logging hook to prevent React error #300

  // Derive loading messages directly (no effect/state needed — removes a
  // deferred setState loop that could cascade with subscription loading).
  const loadingState = React.useMemo(() => {
    if (loading) {
      return {
        title: "Signing you in...",
        subtitle: "Please wait while we prepare your dashboard",
      };
    }
    if (isLoadingSubscription || isLoadingPlans) {
      return {
        title: "Loading your account...",
        subtitle: "Please wait while we load your wallet and preferences",
      };
    }
    return { title: "Loading...", subtitle: "Please wait..." };
  }, [loading, isLoadingSubscription, isLoadingPlans]);

  // Removed logging hook to prevent React error #300

  if (shouldShowLoading) {
    return (
      <HourglassSpinnerWrapper
        fullPage={true}
        title={loadingState.title}
        subtitle={loadingState.subtitle}
      />
    );
  }

  // Don't render protected content if not authenticated (will redirect)
  if (!profile || !isAuthenticated) {
    if (isPublic) {
      // Allow public routes to render
      return <>{children}</>;
    }
    return null;
  }

  const mergedLoadingStates = {
    ...loadingStates,
    ...(sidebarOverrides?.loadingStates ?? {}),
  };

  // Resume requirement modal: show when no resume, except on /resumes. Dismiss hides for current load only.
  const isResumesRoute = location.pathname === "/resumes";
  const showResumeModal =
    !!subscription &&
    !isLoadingSubscription &&
    !isResumesRoute &&
    !hasResume &&
    !isLoadingResumes &&
    !resumeModalDismissed;

  return (
    <div className="main-content" data-has-sidebar="true">
      <ExtensionBanner />
      <ExtensionComingSoonBanner />
      {showResumeModal && (
        <ResumeRequirementModal
          open={true}
          onClose={() => setResumeModalDismissed(true)}
        />
      )}
      {/* Compact Extension Prompt - Top Right Corner (disabled for now)
			<CompactExtensionPrompt /> */}

      {/* Help & Resources Menu - Top Right Corner (unified tour + legal links) */}
      <HelpResourcesMenu />

      {/* Tour Toast - Auto-trigger for first-time users */}
      <TourToast />

      {/* Profile completion guide - visible until 100% */}
      <ProfileCompletionGuide />

      {/* Mobile overlay backdrop - only visible on mobile when menu is open */}
      {isMobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
      <Sidebar
        loadingStates={mergedLoadingStates}
        activeSection={activeSection}
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
      />
      <div className="main-layout">
        {/* Hamburger menu button - only visible on mobile */}
        <button
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
          type="button"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
        <main className="main-body">{children}</main>
      </div>
    </div>
  );
};
